import React from 'react';
import { useLanguage } from '../context/LanguageContext';
import { Container, SectionHeading, Btn } from '../components/ui';
import PageHero from '../components/PageHero';
import Reveal from '../components/Reveal';
import Icon from '../components/Icon';
import CTASection from '../components/CTASection';
import { FeatureCard, CheckList } from '../components/cards';
import { IMPLEMENTATION_PHASES, IMPLEMENTATION_STREAMS } from '../data/content';

export default function HowItWorks() {
  const { t } = useLanguage();
  const goLive = [
    'Signed contract and compliance steps complete',
    'Letter of Authorisation (LOA) reviewed and signed',
    'Portal credentials and supplier mail routes established',
    'Onboarding data for sites, accounts and suppliers reconciled',
    'Integrations built and tested (payments, ERP, sustainability)',
    'Historical data migrated and signed off, UAT passed',
  ];
  return (
    <div>
      <PageHero eyebrow={t('nav.howItWorks')} title="From kickoff to go-live — a proven onboarding journey" subtitle="A structured, managed implementation moves you from contract to live operations with dedicated support at every step.">
        <Btn to="/demo" size="lg" arrow>{t('common.requestDemo')}</Btn>
      </PageHero>

      {/* Phases timeline */}
      <section className="px-5 py-16 sm:px-8 sm:py-24">
        <Container>
          <Reveal><SectionHeading eyebrow="The journey" title="Four phases to go-live" subtitle="Initiation, discovery, execution, then cut-over with dedicated hyper care." /></Reveal>
          <div className="mt-14 grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-4">
            {IMPLEMENTATION_PHASES.map((p, i) => (
              <Reveal key={p.key} delay={i * 80}>
                <div className="relative flex h-full flex-col rounded-2xl border border-slate-200 bg-white p-6 dark:border-white/10 dark:bg-white/[0.03]">
                  <span className="absolute -top-3 left-6 rounded-full bg-brand px-2.5 py-0.5 text-xs font-bold text-white">{i + 1}</span>
                  <span className="mb-4 mt-2 flex h-12 w-12 items-center justify-center rounded-xl bg-brand/10 text-brand"><Icon name={p.icon} className="h-6 w-6" /></span>
                  <h3 className="font-display text-lg font-bold text-slate-900 dark:text-white">{p.title}</h3>
                  <p className="mt-1 text-sm font-medium text-brand">{p.summary}</p>
                  <p className="mt-2 flex-1 text-sm leading-relaxed text-slate-600 dark:text-slate-300">{p.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </Container>
      </section>

      {/* Streams */}
      <section className="bg-slate-50 px-5 py-16 dark:bg-white/[0.02] sm:px-8 sm:py-24">
        <Container>
          <Reveal><SectionHeading eyebrow="Workstreams" title="Every workstream, managed in parallel" subtitle="We run the implementation across coordinated streams so nothing falls through the cracks." /></Reveal>
          <div className="mt-12 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {IMPLEMENTATION_STREAMS.map((s, i) => (<Reveal key={s.title} delay={i * 60}><FeatureCard {...s} /></Reveal>))}
          </div>
        </Container>
      </section>

      {/* Go-live checklist + SLAs */}
      <section className="px-5 py-16 sm:px-8 sm:py-24">
        <Container>
          <div className="grid grid-cols-1 items-start gap-12 lg:grid-cols-2">
            <Reveal>
              <SectionHeading align="left" eyebrow="Cut-over" title="What it takes to go live" subtitle="A clear, reconciled checklist gives everyone confidence at cut-over." />
              <CheckList className="mt-7" items={goLive} />
            </Reveal>
            <Reveal delay={80}>
              <div className="rounded-2xl border border-slate-200 bg-white p-7 dark:border-white/10 dark:bg-white/[0.03]">
                <h3 className="font-display text-lg font-bold text-slate-900 dark:text-white">Hyper care & SLAs</h3>
                <p className="mt-2 text-sm leading-relaxed text-slate-600 dark:text-slate-300">Dedicated teams monitor SLAs across all UBM streams during hyper care and respond immediately to any disruption.</p>
                <div className="mt-6 grid grid-cols-2 gap-4">
                  {[{ v: '24–48h', l: 'Bill collection' }, { v: '24–48h', l: 'Parsing & data' }, { v: '24–48h', l: 'Issue resolution' }, { v: '99%', l: 'Uptime target' }].map((s) => (
                    <div key={s.l} className="rounded-xl bg-slate-50 p-4 dark:bg-white/[0.04]">
                      <div className="font-display text-2xl font-bold text-brand">{s.v}</div>
                      <div className="mt-1 text-xs text-slate-500 dark:text-slate-400">{s.l}</div>
                    </div>
                  ))}
                </div>
              </div>
            </Reveal>
          </div>
        </Container>
      </section>

      <CTASection />
    </div>
  );
}
